import { Brand, DisplayPlacementId } from '@schibsted-nmp/advertising-shared';
import { $config } from '@client/core/atoms/config.js';

const EXPECTED_SENDER = 'high-impact-js';
const EXPECTED_ACTION = 'AD_RENDERED';

const MIDSCROLL_ACTIVE_CLASS = 'seenthis-midscroll';

/* Strict origin whitelist */
function isTrustedOrigin(origin: string): boolean {
  return (
    origin === 'https://tpc.googlesyndication.com' ||
    origin === 'https://securepubads.g.doubleclick.net' ||
    origin.endsWith('.safeframe.googlesyndication.com')
  );
}

function getMidscrollElement(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return document.querySelector<HTMLElement>(
    `[placementid="${DisplayPlacementId.AdvtMiddle4}"]`
  );
}

let shouldActivateMidscroll = false;

export function handleSeenThisSlotRender() {
  shouldActivateMidscroll = true;
}

let isListenerAttached = false;
let messageHandler: ((event: MessageEvent) => void) | null = null;

export function attachHighImpactRenderHandler() {
  if (typeof window === 'undefined') return;

  const { brand } = $config.get() ?? {};
  if (brand !== Brand.Blocket) return;

  if (isListenerAttached) return;
  isListenerAttached = true;

  messageHandler = (event: MessageEvent) => {
    try {
      let payload: unknown = event.data;

      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch {
          console.debug('Ignoring non-JSON payload:', payload);
          return;
        }
      }

      if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return;
      }

      const { sender, action } = payload as {
        sender?: unknown;
        action?: unknown;
      };

      if (sender !== EXPECTED_SENDER || action !== EXPECTED_ACTION) {
        return;
      }

      if (!isTrustedOrigin(event.origin)) {
        console.warn(
          '[SeenThis] Ignored AD_RENDERED — untrusted origin:',
          event.origin
        );
        return;
      }

      if (!shouldActivateMidscroll) {
        return;
      }

      const el = getMidscrollElement();
      if (!el) {
        console.warn(
          '[SeenThis] AD_RENDERED received but Midscroll element not found'
        );
        shouldActivateMidscroll = false;
        return;
      }

      if (!el.classList.contains(MIDSCROLL_ACTIVE_CLASS)) {
        el.classList.add(MIDSCROLL_ACTIVE_CLASS);
      }

      shouldActivateMidscroll = false;
    } catch (err) {
      console.error('[SeenThis] ERROR handling AD_RENDERED:', err);
    }
  };

  window.addEventListener('message', messageHandler);
}

export function cleanupSeenThisHighImpactListener() {
  if (typeof window === 'undefined') return;

  if (messageHandler) {
    window.removeEventListener('message', messageHandler);
    messageHandler = null;
  }

  isListenerAttached = false;
  shouldActivateMidscroll = false;
}
